import { AfterViewChecked, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { AdministrationItemType, DetailsInitInfo, ItemEventInfo } from './data-structures';
import { ClientDTO, PagedResultDTOOfZoneDTO, ZoneDTO } from 'src/app/base/models/models.swagger.generated';
import { ClientService } from 'src/app/base/services/client/client.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TreeViewZoneBasedComponent } from './tree-views/tree-view-zone-based/tree-view-zone-based.component';
import { TreeViewConfigBasedComponent } from './tree-views/tree-view-config-based/tree-view-config-based.component';
import { ITreeViewComponent } from './tree-views/tree-view.interface';
import { AuthService } from 'src/app/base/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { AdministrationDetailsComponent } from './administration-details/administration-details.component';
import { YesNoGroupButtonComponent } from '../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { ClientAutocompleteComponent } from '../../../base/shared/client-autocomplete/client-autocomplete.component';

import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { AutocompleteComponent } from 'src/app/base/shared/autocomplete/autocomplete.component';
import { map, Observable } from 'rxjs';
import { IMultiSelectItem } from 'src/app/base/shared/autocomplete/multi-select-item';
import { ZoneDetailsService } from './administration-details/zone-details/zone-details.service';

@Component({
  selector: 'app-administration',
  templateUrl: './administration.component.html',
  styleUrls: ['./administration.component.scss'],
  imports: [TitleBarComponent, ClientAutocompleteComponent, YesNoGroupButtonComponent, ReactiveFormsModule, TreeViewZoneBasedComponent, TreeViewConfigBasedComponent, AdministrationDetailsComponent, TranslateModule, AutocompleteComponent]
})
export class AdministrationComponent implements AfterViewChecked {
  @ViewChild(TreeViewZoneBasedComponent) public zoneBasedTreeView: TreeViewZoneBasedComponent;
  @ViewChild(TreeViewConfigBasedComponent) public configBasedTreeView: TreeViewConfigBasedComponent;

  public selectedItem: DetailsInitInfo = null;

  public isZoneModeControl: FormControl<boolean> = new FormControl<boolean>(true);

  public hasPendingDetailChanges: boolean = false;

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    this.clientService.selectedClient = value;
    this.onClientSelected(value);
  }

  private zoneCache: ZoneDTO[] = [];
  private _selectedZone: ZoneDTO;
  public get selectedZoneId(): number | null {
    if (this._selectedZone === null) {
      return null;
    }
    return this._selectedZone?.id;
  }

  public set selectedZoneId(value: number) {
    if (this.selectedZoneId != value) {
      let zone = this.zoneCache.find(z => z.id === value);
      this._selectedZone = zone || null;
      // based on the selected zone, select the client
      if (zone) {
        this.selectedClient = this.clientService.clients.find(c => c.id === zone.clientId);
      }
    }
  }

  public get isClientAdminView(): boolean {
    return this.authService.hasUserClientAssigned();
  }

  private get activeTreeView(): ITreeViewComponent {
    return this.isZoneModeControl.value ? this.zoneBasedTreeView : this.configBasedTreeView;
  }

  constructor(private clientService: ClientService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private zoneDetailsService: ZoneDetailsService
  ) {
    this.isZoneModeControl.valueChanges.subscribe((value) => {
      this.selectedItem = null;
    });
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public onItemSelected(data: DetailsInitInfo) {
    this.selectedItem = data;
  }

  public onDetailCancel() {
    if (this.isZoneModeControl.value) {
      this.zoneBasedTreeView?.resetSelection();
    } else {
      this.configBasedTreeView?.resetSelection();
    }
    this.onHasPendingDetailChanges(false);
  }

  public onItemCreated(eventInfo: ItemEventInfo) {
    this.hasPendingDetailChanges = false;
    this.activeTreeView?.addNode(eventInfo);
  }

  public onItemUpdated(eventInfo: ItemEventInfo) {
    this.hasPendingDetailChanges = false;
    this.activeTreeView?.updateNode(eventInfo);
  }

  public onItemDeleted(eventInfo: ItemEventInfo) {
    this.hasPendingDetailChanges = false;
    this.activeTreeView?.removeNode(eventInfo);
  }

  public onHasPendingDetailChanges(hasPendingDetailChanges: boolean) {
    this.hasPendingDetailChanges = hasPendingDetailChanges;
  }

  public onClientChanged(client: ClientDTO) {
    this.selectedClient = client;
    this._selectedZone = null;
    this.zoneCache = [];
    this.selectedZoneId = null;
    this.refreshView();
  }

  private onClientSelected(client: ClientDTO) {
    this.refreshView();
  }

  private refreshView() {
    this.selectedItem = null;
    this.hasPendingDetailChanges = false;
    setTimeout(() => {
      // We need to call this in separate thread, otherwise the view does not clear properly
      try {
        this.activeTreeView?.reset();
      } catch { }

    }, 0);
  }

  public searchZone(searchText: string): Observable<IMultiSelectItem[]> {
    return this.zoneDetailsService.searchZone(searchText).pipe(
      map((data: PagedResultDTOOfZoneDTO) => {
        this.zoneCache = data.items;
        return data.items.map((zone: ZoneDTO) => {
          return { id: zone.id, name: zone.name };
        });
      })
    );
  }
}
